import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AccountIdentity } from "@common/helpers/rules";
import { Entity } from "@common/types/entity";
import { useEntities } from "@hooks/useEntities";
import { useWorkspaceAccounts } from "@api/account.api";
import { useDefaultAccountingRules } from "@api/rules.api";
import {
    CategoryGlMappingDirection,
    DefaultsByEntityId,
    WorkspaceAccount,
    areIdentitiesEqual,
    buildDefaultsByEntityId,
    getSharedDefaultIdentity,
    resolveEntityAccountIdByIdentity,
} from "./categoryGlMappingGrid.utils";

export interface CategoryGlMappingOverrideRow {
    id: string;
    entityIds: number[];
    in: AccountIdentity | null;
    out: AccountIdentity | null;
}

export interface CategoryGlMappingOverridePayload {
    entityId: number;
    inAccountId: string | null;
    outAccountId: string | null;
}

interface UseCategoryGlMappingGridParams {
    parentCategoryId: string;
    isEnabled?: boolean;
    initialRows?: CategoryGlMappingOverrideRow[];
}

export interface UseCategoryGlMappingGridResult {
    rows: CategoryGlMappingOverrideRow[];
    defaultRowEntityIds: number[];
    entities: Entity[];
    accounts: WorkspaceAccount[];
    defaults: DefaultsByEntityId;
    isLoading: boolean;
    addRow: () => void;
    removeRow: (rowId: string) => void;
    setRowEntities: (rowId: string, entityIds: number[]) => void;
    setRowAccount: (
        rowId: string,
        direction: CategoryGlMappingDirection,
        identity: AccountIdentity | null,
    ) => void;
    toOverridesPayload: () => CategoryGlMappingOverridePayload[];
}

let rowIdCounter = 0;
function nextRowId(): string {
    rowIdCounter += 1;
    return `override-row-${rowIdCounter}`;
}

function clearAccountsNotSharedAcrossEntities(
    row: CategoryGlMappingOverrideRow,
    accounts: WorkspaceAccount[],
): CategoryGlMappingOverrideRow {
    const isIdentityShared = (identity: AccountIdentity | null) =>
        identity !== null &&
        row.entityIds.every(
            (entityId) =>
                resolveEntityAccountIdByIdentity({
                    accounts,
                    entityId,
                    identity,
                }) !== null,
        );

    return {
        ...row,
        in: isIdentityShared(row.in) ? row.in : null,
        out: isIdentityShared(row.out) ? row.out : null,
    };
}

interface ResolveOverrideAccountIdParams {
    row: CategoryGlMappingOverrideRow;
    entityId: number;
    direction: CategoryGlMappingDirection;
    accounts: WorkspaceAccount[];
    defaults: DefaultsByEntityId;
}

function resolveOverrideAccountId({
    row,
    entityId,
    direction,
    accounts,
    defaults,
}: ResolveOverrideAccountIdParams): string | null {
    const identity = row[direction];
    if (!identity) {
        return null;
    }
    const defaultIdentity = defaults.get(entityId)?.[direction] ?? null;
    if (areIdentitiesEqual(identity, defaultIdentity)) {
        return null;
    }
    return resolveEntityAccountIdByIdentity({ accounts, entityId, identity });
}

interface ApplyRowEntitiesParams {
    current: CategoryGlMappingOverrideRow[];
    rowId: string;
    nextEntityIds: number[];
    accounts: WorkspaceAccount[];
    defaults: DefaultsByEntityId;
}

interface MoveEntitiesToRowParams {
    row: CategoryGlMappingOverrideRow;
    rowId: string;
    nextEntityIds: number[];
    addedEntityIds: number[];
}

function moveEntitiesToRow({
    row,
    rowId,
    nextEntityIds,
    addedEntityIds,
}: MoveEntitiesToRowParams): CategoryGlMappingOverrideRow {
    if (row.id === rowId) {
        return { ...row, entityIds: nextEntityIds };
    }
    if (addedEntityIds.length === 0) {
        return row;
    }
    return {
        ...row,
        entityIds: row.entityIds.filter((id) => !addedEntityIds.includes(id)),
    };
}

function applyRowEntities({
    current,
    rowId,
    nextEntityIds,
    accounts,
    defaults,
}: ApplyRowEntitiesParams): CategoryGlMappingOverrideRow[] {
    const targetRow = current.find((row) => row.id === rowId);
    if (!targetRow) {
        return current;
    }
    const addedEntityIds = nextEntityIds.filter(
        (id) => !targetRow.entityIds.includes(id),
    );

    return current.map((row) => {
        const moved = moveEntitiesToRow({
            row,
            rowId,
            nextEntityIds,
            addedEntityIds,
        });
        const validated = clearAccountsNotSharedAcrossEntities(moved, accounts);
        return row.id === rowId
            ? prefillFromDefaults(validated, defaults)
            : validated;
    });
}

interface RowToOverridePayloadParams {
    row: CategoryGlMappingOverrideRow;
    accounts: WorkspaceAccount[];
    defaults: DefaultsByEntityId;
}

function rowToOverridePayload({
    row,
    accounts,
    defaults,
}: RowToOverridePayloadParams): CategoryGlMappingOverridePayload[] {
    return row.entityIds.map((entityId) => ({
        entityId,
        inAccountId: resolveOverrideAccountId({
            row,
            entityId,
            direction: "in",
            accounts,
            defaults,
        }),
        outAccountId: resolveOverrideAccountId({
            row,
            entityId,
            direction: "out",
            accounts,
            defaults,
        }),
    }));
}

function prefillFromDefaults(
    row: CategoryGlMappingOverrideRow,
    defaults: DefaultsByEntityId,
): CategoryGlMappingOverrideRow {
    if (row.entityIds.length === 0) {
        return row;
    }
    const sharedFor = (direction: CategoryGlMappingDirection) =>
        getSharedDefaultIdentity({
            entityIds: row.entityIds,
            direction,
            defaults,
        });
    return {
        ...row,
        in: row.in ?? sharedFor("in"),
        out: row.out ?? sharedFor("out"),
    };
}

export function useCategoryGlMappingGrid({
    parentCategoryId,
    isEnabled = true,
    initialRows,
}: UseCategoryGlMappingGridParams): UseCategoryGlMappingGridResult {
    const entities = useEntities({
        onlyBusiness: true,
        excludeMockEntities: true,
    });
    const workspaceAccounts = useWorkspaceAccounts({ enabled: isEnabled });
    const defaultAccountingRules = useDefaultAccountingRules({
        enabled: isEnabled,
    });

    const accounts = useMemo<WorkspaceAccount[]>(
        () => workspaceAccounts.data ?? [],
        [workspaceAccounts.data],
    );

    const entityIds = useMemo(
        () => entities.map((entity) => entity.id),
        [entities],
    );

    const defaults = useMemo<DefaultsByEntityId>(
        () =>
            buildDefaultsByEntityId({
                parentCategoryId,
                entityIds,
                defaultRules:
                    defaultAccountingRules.data?.ruleGroup.rules ?? [],
                accounts,
            }),
        [parentCategoryId, entityIds, defaultAccountingRules.data, accounts],
    );

    const [rows, setRows] = useState<CategoryGlMappingOverrideRow[]>(
        initialRows ?? [],
    );

    const hasSeededRef = useRef(initialRows !== undefined);
    useEffect(() => {
        if (!hasSeededRef.current && initialRows !== undefined) {
            setRows(initialRows);
            hasSeededRef.current = true;
        }
    }, [initialRows]);

    const defaultRowEntityIds = useMemo(() => {
        const overriddenEntityIds = new Set(
            rows.flatMap((row) => row.entityIds),
        );
        return entityIds.filter((id) => !overriddenEntityIds.has(id));
    }, [entityIds, rows]);

    const addRow = useCallback(() => {
        setRows((current) => [
            ...current,
            { id: nextRowId(), entityIds: [], in: null, out: null },
        ]);
    }, []);

    const removeRow = useCallback((rowId: string) => {
        setRows((current) => current.filter((row) => row.id !== rowId));
    }, []);

    const setRowEntities = useCallback(
        (rowId: string, nextEntityIds: number[]) => {
            setRows((current) =>
                applyRowEntities({
                    current,
                    rowId,
                    nextEntityIds,
                    accounts,
                    defaults,
                }),
            );
        },
        [accounts, defaults],
    );

    const setRowAccount = useCallback(
        (
            rowId: string,
            direction: CategoryGlMappingDirection,
            identity: AccountIdentity | null,
        ) => {
            setRows((current) =>
                current.map((row) =>
                    row.id === rowId ? { ...row, [direction]: identity } : row,
                ),
            );
        },
        [],
    );

    const toOverridesPayload = useCallback(
        (): CategoryGlMappingOverridePayload[] =>
            rows.flatMap((row) =>
                rowToOverridePayload({ row, accounts, defaults }),
            ),
        [rows, accounts, defaults],
    );

    return {
        rows,
        defaultRowEntityIds,
        entities,
        accounts,
        defaults,
        isLoading:
            workspaceAccounts.isLoading || defaultAccountingRules.isLoading,
        addRow,
        removeRow,
        setRowEntities,
        setRowAccount,
        toOverridesPayload,
    };
}
